import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import BANNERS_API_V1 from 'api/banners/v1'
import styles from './slider-banner.module.scss'
import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import Buttons from 'components/Banners/SliderBanner/subcomponents/Buttons'
import clsx from 'clsx'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import '@splidejs/react-splide/dist/css/splide-core.min.css'
import { Transition } from 'components/Banners/SliderBanner/Transition'

const OPTIONS = {
  type: 'slide',
  perPage: 1,
  autoplay: true,
  gap: '1rem',
  arrows: true,
  pagination: true,
  rewind: true,
  rewindSpeed: 500,
  classes: {
    page: clsx('splide__pagination__page', styles['slide-banner__splide-page']),
  },
}

const SliderBanner = ({ className, placement, network = null }) => {
  const { i18n } = useSelector(({ app }) => app)
  const [banners, setBanners] = useState([])
  const [currentBannerNum, setCurrentBannerNum] = useState(0)
  const [loading, setLoading] = useState(true)
  const breakpoints = useBreakpoint()
  const splideRef = React.useRef(null)

  useEffect(() => {
    if (!network) {
      return
    }

    setBanners([])
    setLoading(true)

    BANNERS_API_V1.fetchBanners({
      params: { placement, network, locale: i18n },
    })
      .then(({ response }) => {
        setBanners(response?.data || [])
        setCurrentBannerNum(0)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [i18n, network])

  const currentBanner = useMemo(() => banners[currentBannerNum] ?? {}, [banners, currentBannerNum])

  const fontSizes = useMemo(() => {
    switch (true) {
      case breakpoints.sm:
        return { title: 'Subtitle-20', text: 'Body-15' }
      case !breakpoints.mdl:
        return { title: 'Subtitle-40', text: 'Body-20' }
      default:
        return { title: 'Subtitle-30', text: 'Body-15' }
    }
  }, [breakpoints])

  const handleMove = (ctx, newIndex) => {
    setCurrentBannerNum(newIndex)
  }

  const handleMouseEnter = () => {
    if (!splideRef.current) return
    splideRef.current.splide.Components.Autoplay.pause()
  }

  const handleMouseLeave = () => {
    if (!splideRef.current) return
    splideRef.current.splide.Components.Autoplay.play()
  }

  if (!loading && banners.length === 0) {
    return null
  }

  return loading ? null : (
    <div
      className={styles['slide-banner']}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Splide
        ref={splideRef}
        hasTrack={false}
        className={styles['slide-banner__splide']}
        transition={Transition}
        options={OPTIONS}
        onMove={handleMove}
      >
        <SplideTrack>
          {banners.map(({ image_desktop, image_tablet, image_mobile, image, title, text, id }) => (
            <SplideSlide
              key={id}
              className={styles['slide-banner__splide-item']}
            >
              <div className={clsx(styles['slide-banner__container'], className)}>
                <div className={styles['slide-banner__background']}>
                  <picture>
                    <source
                      media="(min-width: 1280px)"
                      srcSet={image_desktop}
                    />
                    <source
                      media="(min-width: 768px)"
                      srcSet={image_tablet}
                    />
                    <source
                      media="(max-width: 767px)"
                      srcSet={image_mobile}
                    />

                    <img
                      src={image_desktop || image}
                      alt={'pic'}
                    />
                  </picture>
                </div>
                <Grid
                  className={styles['slide-banner__content']}
                  direction={'column'}
                  gap={24}
                  justifyContent={'space-between'}
                >
                  <Grid
                    direction={'column'}
                    justifyContent={'space-between'}
                  >
                    <Grid
                      direction={'column'}
                      gap={breakpoints.sm ? 8 : 12}
                    >
                      <Typography
                        variant={fontSizes.title}
                        color={'Label-Fifth'}
                        className={styles['slide-banner__title']}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant={fontSizes.text}
                        component={'div'}
                      >
                        {text}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </SplideSlide>
          ))}
        </SplideTrack>
        <Grid
          wrap={false}
          fluid={false}
          alignItems={'center'}
          direction={'row'}
          className={clsx('splide__arrows', styles['slide-banner__navigations'])}
          gap={8}
        >
          <Grid
            gap={16}
            direction={'row'}
            wrap={false}
            fluid={false}
          >
            <Buttons currentBanner={currentBanner} />
          </Grid>
          <Button
            className={clsx('splide__arrow splide__arrow--prev', styles['slide-banner__arrow'])}
            color={'Interface-Seventh'}
            noLabel
            size={'s'}
            variant={'ghost'}
            grey
            link
            startIcon={
              <Icon
                name={'arrow-left'}
                size={'m'}
                color={'Interface-Sixth'}
              />
            }
          ></Button>
          <Grid
            gap={4}
            direction={'row'}
            wrap={false}
            fluid={false}
            component={'ul'}
            className={clsx('splide__pagination', styles['slide-banner__splide-pagination'])}
          />

          <Button
            className="splide__arrow splide__arrow--next"
            color={'Interface-Seventh'}
            noLabel
            size={'s'}
            variant={'ghost'}
            grey
            link
            startIcon={
              <Icon
                name={'arrow-right'}
                size={'m'}
                color={'Interface-Sixth'}
              />
            }
          />
        </Grid>
      </Splide>
    </div>
  )
}

export default SliderBanner

SliderBanner.propTypes = {
  placement: PropTypes.string,
  network: PropTypes.string,
  className: PropTypes.string,
}
